import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Button, Divider, Chip } from '@mui/material';
import { LocationOn, Phone, Email, Business } from '@mui/icons-material';
import { facilityApi } from '../../features/facilities/api/facilityApi';
import { Facility } from '../../features/facilities/types/facility.types';

const FacilityView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [facility, setFacility] = useState<Facility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFacility = async () => {
      setLoading(true);
      try {
        if (!id) return;
        const data = await facilityApi.getById(id);
        setFacility(data);
      } catch (err) {
        setError('Failed to load facility details.');
      } finally {
        setLoading(false);
      }
    };
    fetchFacility();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !facility) {
    return (
      <Box textAlign="center" py={6}>
        <Typography color="error" variant="h6">{error || 'Facility not found.'}</Typography>
        <Button onClick={() => navigate('/facilities')} variant="contained" sx={{ mt: 2 }}>Back to Facilities</Button>
      </Box>
    );
  }

  return (
    <Box maxWidth={600} mx="auto" mt={4}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {facility.name}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box mb={2}>
          <Chip label={facility.isActive ? 'Active' : 'Inactive'} color={facility.isActive ? 'success' : 'default'} sx={{ mr: 1 }} />
          {facility.type && <Chip label={facility.type} variant="outlined" sx={{ mr: 1 }} />}
        </Box>
        <Box display="flex" alignItems="center" mb={1}>
          <LocationOn sx={{ mr: 1 }} color="action" />
          <Typography variant="body2">
            {[facility.address, facility.city, facility.state, facility.country].filter(Boolean).join(', ') || 'N/A'}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" mb={1}>
          <Phone sx={{ mr: 1 }} color="action" />
          <Typography variant="body2">{facility.phoneNumber || 'N/A'}</Typography>
        </Box>
        <Box display="flex" alignItems="center" mb={1}>
          <Email sx={{ mr: 1 }} color="action" />
          <Typography variant="body2">{facility.email || 'N/A'}</Typography>
        </Box>
        {facility.hospital?.name && (
          <Box display="flex" alignItems="center" mb={1}>
            <Business sx={{ mr: 1 }} color="action" />
            <Typography variant="body2"><strong>Hospital:</strong> {facility.hospital.name}</Typography>
          </Box>
        )}
        <Divider sx={{ my: 2 }} />
        <Button variant="outlined" onClick={() => navigate('/facilities')}>Back to Facilities</Button>
      </Paper>
    </Box>
  );
};

export default FacilityView;
